import { useState, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './VideoPlayer.css';

// R2 CDN base URL
const R2_CDN_URL = 'https://cdn.solarmaktab.com';

export interface VideoPlayerProps {
  src: string;                // R2 path (relative) or full URL
  title: string;
  titleFa?: string;
  onEnded?: () => void;
  onError?: (error: Error) => void;
}

export function VideoPlayer({ src, title, titleFa, onEnded, onError }: VideoPlayerProps) {
  const { i18n } = useTranslation();
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  
  // Player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showControls, setShowControls] = useState(true);
  const [buffered, setBuffered] = useState(0);
  
  const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const isRTL = i18n.language === 'fa';
  const displayTitle = isRTL && titleFa ? titleFa : title;
  
  // Build full video URL
  const videoUrl = src.startsWith('http') ? src : `${R2_CDN_URL}/${src}`;

  // Format time as MM:SS or HH:MM:SS
  const formatTime = (seconds: number): string => {
    if (isNaN(seconds) || !isFinite(seconds)) return '0:00';
    
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = Math.floor(seconds % 60);
    
    if (hrs > 0) {
      return `${hrs}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Handle play/pause toggle
  const togglePlay = useCallback(() => {
    if (!videoRef.current) return;
    
    if (isPlaying) {
      videoRef.current.pause();
    } else {
      videoRef.current.play().catch(err => {
        console.error('Error playing video:', err);
        setError(isRTL ? 'خطا در پخش ویدیو' : 'Error playing video');
        onError?.(err);
      });
    }
  }, [isPlaying, isRTL, onError]);

  // Handle seek
  const handleSeek = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (!progressRef.current || !videoRef.current || duration <= 0) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percentage = Math.max(0, Math.min(1, x / rect.width));
    const newTime = percentage * duration;
    
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Handle volume change
  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    
    if (videoRef.current) {
      videoRef.current.volume = newVolume;
    }
    
    if (newVolume === 0) {
      setIsMuted(true);
    } else if (isMuted) {
      setIsMuted(false);
    }
  }, [isMuted]);

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (!videoRef.current) return;
    
    const newMuted = !isMuted;
    videoRef.current.muted = newMuted;
    setIsMuted(newMuted);
  }, [isMuted]);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(async () => {
    if (!containerRef.current) return;
    
    try {
      if (!document.fullscreenElement) {
        await containerRef.current.requestFullscreen();
        setIsFullscreen(true);
      } else {
        await document.exitFullscreen();
        setIsFullscreen(false);
      }
    } catch (err) {
      console.error('Fullscreen error:', err);
    }
  }, []);

  // Skip forward/backward
  const skip = useCallback((seconds: number) => {
    if (!videoRef.current) return;
    
    const newTime = Math.max(0, Math.min(duration, videoRef.current.currentTime + seconds));
    videoRef.current.currentTime = newTime;
    setCurrentTime(newTime);
  }, [duration]);

  // Retry loading video
  const handleRetry = useCallback(() => {
    setError(null);
    setIsLoading(true);
    
    if (videoRef.current) {
      videoRef.current.load();
    }
  }, []);

  // Show controls temporarily
  const showControlsTemporarily = useCallback(() => {
    setShowControls(true);
    
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }
    
    if (isPlaying) {
      controlsTimeoutRef.current = setTimeout(() => {
        setShowControls(false);
      }, 3000);
    }
  }, [isPlaying]);

  // Video event handlers
  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    showControlsTemporarily();
  }, [showControlsTemporarily]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
    setShowControls(true);
  }, []);

  const handleTimeUpdate = useCallback(() => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  }, []);

  const handleLoadedMetadata = useCallback(() => {
    if (videoRef.current) {
      setDuration(videoRef.current.duration);
      setIsLoading(false);
    }
  }, []);

  const handleProgress = useCallback(() => {
    if (videoRef.current && videoRef.current.buffered.length > 0) {
      const bufferedEnd = videoRef.current.buffered.end(videoRef.current.buffered.length - 1);
      const bufferedPercent = (bufferedEnd / videoRef.current.duration) * 100;
      setBuffered(bufferedPercent);
    }
  }, []);

  const handleEnded = useCallback(() => {
    setIsPlaying(false);
    setShowControls(true);
    onEnded?.();
  }, [onEnded]);

  const handleVideoError = useCallback(() => {
    const errorMessage = isRTL 
      ? 'خطا در بارگذاری ویدیو. لطفاً دوباره تلاش کنید.'
      : 'Error loading video. Please try again.';
    setError(errorMessage);
    setIsLoading(false);
    onError?.(new Error(errorMessage));
  }, [isRTL, onError]);

  const handleWaiting = useCallback(() => {
    setIsLoading(true);
  }, []);

  const handleCanPlay = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Only handle if video player is focused or in fullscreen
      if (!containerRef.current?.contains(document.activeElement) && !isFullscreen) {
        return;
      }
      
      switch (e.key) {
        case ' ':
        case 'k':
          e.preventDefault();
          togglePlay();
          break;
        case 'ArrowLeft':
          e.preventDefault();
          skip(-10);
          break;
        case 'ArrowRight':
          e.preventDefault();
          skip(10);
          break;
        case 'ArrowUp':
          e.preventDefault();
          setVolume(v => Math.min(1, v + 0.1));
          if (videoRef.current) videoRef.current.volume = Math.min(1, volume + 0.1);
          break;
        case 'ArrowDown':
          e.preventDefault();
          setVolume(v => Math.max(0, v - 0.1));
          if (videoRef.current) videoRef.current.volume = Math.max(0, volume - 0.1);
          break;
        case 'm':
          e.preventDefault();
          toggleMute();
          break;
        case 'f':
          e.preventDefault();
          toggleFullscreen();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [togglePlay, skip, toggleMute, toggleFullscreen, isFullscreen, volume]);

  // Handle fullscreen change events
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, []);

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <div 
      ref={containerRef}
      className={`video-player ${isRTL ? 'rtl' : 'ltr'} ${isFullscreen ? 'fullscreen' : ''}`}
      onMouseMove={showControlsTemporarily}
      onMouseLeave={() => isPlaying && setShowControls(false)}
      tabIndex={0}
    >
      {/* Video Title */}
      <div className={`vp-title-bar ${showControls ? 'visible' : ''}`}>
        <h2 className="vp-title">{displayTitle}</h2>
      </div>

      {/* Video Element */}
      <video
        ref={videoRef}
        className="vp-video"
        src={videoUrl}
        onClick={togglePlay}
        onPlay={handlePlay}
        onPause={handlePause}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onProgress={handleProgress}
        onEnded={handleEnded}
        onError={handleVideoError}
        onWaiting={handleWaiting}
        onCanPlay={handleCanPlay}
        playsInline
        preload="metadata"
      />

      {/* Loading Spinner */}
      {isLoading && !error && (
        <div className="vp-loading">
          <div className="vp-spinner"></div>
          <span>{isRTL ? 'در حال بارگذاری...' : 'Loading...'}</span>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="vp-error">
          <div className="vp-error-icon">⚠️</div>
          <p className="vp-error-message">{error}</p>
          <button className="vp-retry-btn" onClick={handleRetry}>
            🔄 {isRTL ? 'تلاش مجدد' : 'Retry'}
          </button>
        </div>
      )}

      {/* Play Button Overlay (when paused) */}
      {!isPlaying && !isLoading && !error && (
        <button className="vp-play-overlay" onClick={togglePlay} aria-label="Play">
          <span className="vp-play-icon">▶</span>
        </button>
      )}

      {/* Controls */}
      <div className={`vp-controls ${showControls ? 'visible' : ''}`}>
        {/* Progress Bar */}
        <div 
          className="vp-progress-container" 
          ref={progressRef}
          onClick={handleSeek}
        >
          <div className="vp-progress-bar">
            <div className="vp-buffered" style={{ width: `${buffered}%` }} />
            <div className="vp-progress-fill" style={{ width: `${progress}%` }} />
            <div className="vp-progress-handle" style={{ left: `${progress}%` }} />
          </div>
        </div>

        {/* Control Buttons */}
        <div className="vp-controls-row">
          <div className="vp-controls-left">
            {/* Play/Pause */}
            <button className="vp-control-btn" onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'}>
              {isPlaying ? '⏸️' : '▶️'}
            </button>

            {/* Skip Backward */}
            <button className="vp-control-btn" onClick={() => skip(-10)} aria-label="Skip back 10 seconds">
              ⏪
            </button>

            {/* Skip Forward */}
            <button className="vp-control-btn" onClick={() => skip(10)} aria-label="Skip forward 10 seconds">
              ⏩
            </button>

            {/* Volume */}
            <div className="vp-volume-container">
              <button className="vp-control-btn" onClick={toggleMute} aria-label={isMuted ? 'Unmute' : 'Mute'}>
                {isMuted || volume === 0 ? '🔇' : volume < 0.5 ? '🔉' : '🔊'}
              </button>
              <input
                type="range"
                min="0"
                max="1"
                step="0.05"
                value={isMuted ? 0 : volume}
                onChange={handleVolumeChange}
                className="vp-volume-slider"
                aria-label="Volume"
              />
            </div>

            {/* Time Display */}
            <div className="vp-time">
              <span>{formatTime(currentTime)}</span>
              <span className="vp-time-separator">/</span>
              <span>{formatTime(duration)}</span>
            </div>
          </div>

          <div className="vp-controls-right">
            {/* Fullscreen */}
            <button className="vp-control-btn" onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}>
              {isFullscreen ? '⛶' : '⛶'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
